import { actions } from "@/actions";
import { MDSCollectionIcon, MDSIconKind, MDSMenuContentItemKind, MDSMenuContentList } from "@/mds";
import { EventContext } from "@/domains/metrics/context";
import { SearchSuggestionType } from "@/domains/search";
import { uuidModule } from "@/modules/uuid";
import { CollectionObservable } from "@/store/collections/CollectionObservable";
import { INoteObservable } from "@/store/note";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { filter } from "lodash-es";
import { action, computed, makeObservable, observable, reaction } from "mobx";

export class AddToCollectionModalStore extends AppSubStore {
  private handleConfirm?: () => void;
  public collection?: CollectionObservable;
  public selectedNoteIds: string[] = [];
  public searchQuery: string;
  public keyboardSelectedIndex = -1;
  public displayedCollections: CollectionObservable[] = [];
  public eventContext?: EventContext;

  constructor(injectedDeps: AppSubStoreArgs, { handleConfirm }: { handleConfirm?: () => void }) {
    super(injectedDeps);

    this.handleConfirm = handleConfirm;
    this.searchQuery = "";

    makeObservable<this, "handleConfirm">(this, {
      handleConfirm: false,

      displayedCollections: observable,
      collection: observable,
      selectedNoteIds: observable,
      searchQuery: observable,
      keyboardSelectedIndex: observable,
      eventContext: observable,

      isOpen: computed,
      selectedNote: computed,
      selectedNotes: computed,
      dropdownContentList: computed,

      open: action,
      close: action,
      setSearchQuery: action,
      resetSelectedIndex: action,
      setDisplayedCollections: action,
      queryCollections: action,
    });

    reaction(() => this.searchQuery, this.queryCollections);
  }

  get isOpen() {
    return this.selectedNoteIds.length > 0;
  }

  get selectedNote(): INoteObservable | undefined {
    return this.selectedNotes[0];
  }

  get selectedNotes() {
    return filter(
      this.selectedNoteIds.map(noteId => this.store.notes.getNoteObservableById({ noteId }))
    ) as INoteObservable[];
  }

  get dropdownContentList(): MDSMenuContentList {
    const output: MDSMenuContentList = [];

    const itemIds = this.selectedNoteIds;
    const items = this.selectedNotes;
    if (!itemIds.length || !items.length) return output;

    const displayedCollections = this.displayedCollections;
    if (displayedCollections.length > 0) {
      output.push({
        id: "collections-title",
        kind: MDSMenuContentItemKind.Header,
        label: "Collections",
      });

      displayedCollections.forEach((collection, index) => {
        output.push({
          id: `collection-${collection.id}`,
          kind: MDSMenuContentItemKind.Item,
          icon: () => <MDSCollectionIcon size={11} collectionId={collection.id} />,
          label: collection.label,
          isSelected: index + 1 === this.keyboardSelectedIndex,
          onClick: () => {
            actions.addNotesToCollection({
              notes: items,
              collection,
              store: this.store,
              eventContext: this.eventContext ?? EventContext.Unknown,
            });
            this.handleConfirm?.();
            this.close();
          },
          subtitle: collection.subtitle,
        });
      });
    }

    if (this.searchQuery.length > 0) {
      output.push({
        id: "create-collection-title",
        kind: MDSMenuContentItemKind.Header,
        label: "Create collection",
      });
      output.push({
        id: "create-collection-button",
        kind: MDSMenuContentItemKind.Item,
        icon: MDSIconKind.Plus,
        label: `Collection named ${this.searchQuery}`,
        isSelected: this.keyboardSelectedIndex > displayedCollections.length,
        onClick: async () => {
          const collectionId = uuidModule.generate();
          await this.store.collections.createCollection({
            collectionId,
            title: this.searchQuery,
            eventContext: this.eventContext ?? EventContext.Unknown,
          });
          const collectionObservable = await this.store.collections.getAsync(collectionId);
          actions.addNotesToCollection({
            notes: items,
            collection: collectionObservable!,
            store: this.store,
            eventContext: this.eventContext ?? EventContext.Unknown,
          });
          this.handleConfirm?.();
          this.close();
        },
      });
    }
    return output;
  }

  public open({
    noteIds,
    collection,
    eventContext,
  }: {
    noteIds: string[];
    collection?: CollectionObservable;
    eventContext: EventContext;
  }) {
    this.selectedNoteIds = noteIds;
    this.collection = collection;
    this.eventContext = eventContext;
  }

  public close() {
    this.selectedNoteIds = [];
    this.collection = undefined;
    this.eventContext = undefined;
  }

  public setSearchQuery(query: string) {
    this.searchQuery = query;
    this.resetSelectedIndex();
  }

  resetSelectedIndex() {
    this.keyboardSelectedIndex = 1;
  }

  setDisplayedCollections(collections: CollectionObservable[]) {
    this.displayedCollections = collections;
  }

  queryCollections = async (query: string) => {
    const suggestions = await this.store.search.forSuggestions(
      query,
      "default",
      item => item.type === SearchSuggestionType.COLLECTION && item.modelId !== this.collection?.id,
      5
    );

    const collections = (
      await Promise.all(
        suggestions.map(suggestion => this.store.collections.getAsync(suggestion.modelId))
      )
    ).filter(Boolean) as CollectionObservable[]; // there should be no situation where we can't initialize a collection

    this.setDisplayedCollections(collections);
  };
}
